import React, { useEffect, useState } from 'react'
import { styled } from 'linaria/react'
import { ReactComponent as PdfIcon } from '../../../svg/pdf.svg'
import { useIntl } from '@jetshop/intl'
import { ProductAccordion } from './ProductAccordion'
import {
   createGroupedItemsByTypeObj,
   isArrayWithItems,
   isObjectWithKeys,
} from '../../Core/Util/Helpers'
import VbDrawerPdf from '../../Core/VbDrawerPdf'
import { useIsAboveLg } from '../../Core/Hooks/mediaQueryHooks'
import { MANUAL_TYPE_NAME } from '../../Core/Util/Constants'

const ProductSpecificationContainer = styled.div`
   .product-specification .list-group-item {
      border: 0;
      border-radius: 0;
      border-bottom-left-radius: 0 !important;
      border-top-right-radius: 0 !important;
      background-color: transparent;
   }

   .product-specification .list-group .list-group-item:first-child {
      font-weight: 700;
      font-family: var(--font-industry);
      text-transform: uppercase;
      width: 30%;
      min-width: 12rem;
   }

   .product-specification .list-group .list-group-item:last-child {
      width: 70%;
   }

   .product-specification .list-group:nth-child(odd) li {
      background: var(--color-vb-gray-2) !important;
   }

   ul {
      padding: unset;
      margin: unset;
      list-style: none;
   }

   @media (max-width: 768px) {
      .list-group li {
         width: 50% !important;
      }
      .list-group-item:last-child {
         padding-left: 0;
      }
   }
`

const FilelistContainer = styled.div`
   svg {
      width: 26px;
   }
`

const TabList = styled.ul`
   list-style-type: none;
   padding: 0;
   margin: 0;
   display: flex;
   border-bottom: 1px solid #dee2e6;
   overflow-x: auto;
   -webkit-overflow-scrolling: touch;
   &::-webkit-scrollbar {
      display: none;
   }
   -ms-overflow-style: none;
   scrollbar-width: none;

   @media (max-width: 768px) {
      flex-wrap: nowrap;
      overflow-x: auto;
   }
`

const TabItem = styled.li`
   font-family: var(--font-industry);
   padding: 10px 15px;
   cursor: pointer;
   border: 1px solid transparent;
   border-top-left-radius: 0.25rem;
   border-top-right-radius: 0.25rem;
   color: ${props => (props.active ? '#495057' : 'inherit')};
   opacity: ${props => (props.active ? 1 : 0.7)};
   background-color: ${props => (props.active ? '#fff' : 'transparent')};
   font-weight: ${props => (props.active ? '700' : 'normal')};
   border-color: ${props =>
      props.active ? '#dee2e6 #dee2e6 #fff' : 'transparent'};
   &:hover {
      border-color: #e9ecef #e9ecef #dee2e6;
      opacity: 1;
   }

   @media (max-width: 768px) {
      flex: 1 0 0%;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: auto;
      padding: 10px 8px;
   }
`

const ProductSpecification = ({ productInformation }) => {
   const t = useIntl()
   const { manuals, specifications, ean, bundleProducts, isPackage } =
      productInformation ?? {}
   const [activeTab, setActiveTab] = useState(0)

   const translateGroupedTitles = type => {
      const typeMap = {
         [MANUAL_TYPE_NAME.SAFETY_SHEET]: t('safetysheet'),
         [MANUAL_TYPE_NAME.MANUAL]: t('manual'),
         [MANUAL_TYPE_NAME.DIMENSIONAL_SHEET]: t('dimensionalsheet'),
      }
      return typeMap[type] || type
   }

   const sortSpecifications = specifications => {
      return (Array.isArray(specifications) ? specifications : []).sort(
         (a, b) => {
            const nameA = (a?.name ?? '').toUpperCase()
            const nameB = (b?.name ?? '').toUpperCase()
            if (
               nameA.includes('FÖRPACKNINGS') &&
               !nameB.includes('FÖRPACKNINGS')
            )
               return 1
            if (
               !nameA.includes('FÖRPACKNINGS') &&
               nameB.includes('FÖRPACKNINGS')
            )
               return -1
            return nameA.localeCompare(nameB)
         }
      )
   }

   const getGroupedManualsByType = manuals =>
      createGroupedItemsByTypeObj(
         manuals,
         'Type',
         'Files',
         translateGroupedTitles
      )

   const specificationItem = isPackage
      ? null
      : {
           specifications: [
              ...sortSpecifications(specifications),
              ...(ean ? [{ name: 'EAN', value: ean, unit: '' }] : []),
           ],
           manuals: getGroupedManualsByType(manuals),
        }

   const specificationItems = isPackage
      ? bundleProducts?.map(product => ({
           title: product?.name,
           specifications: sortSpecifications(product?.specifications),
           manuals: getGroupedManualsByType(product?.manuals),
        }))
      : null

   const shouldRenderSpecifications = isPackage
      ? specificationItems?.length > 0
      : specificationItem?.specifications?.length > 0

   return (
      <>
         {shouldRenderSpecifications && (
            <>
               <hr className="my-32"></hr>
               <ProductAccordion isOpen={true} title={t('specifications')}>
                  <ProductSpecificationContainer>
                     <div className="product-specification">
                        {isPackage ? (
                           <div>
                              <TabList>
                                 {specificationItems?.map(
                                    (item, index) => (
                                       <TabItem
                                          key={index}
                                          active={activeTab === index}
                                          onClick={() => setActiveTab(index)}
                                       >
                                          {item.title}
                                       </TabItem>
                                    )
                                 )}
                              </TabList>
                              <div>
                                 {specificationItems?.[
                                    activeTab
                                 ]?.specifications?.map((spec, index) => (
                                    <ul
                                       key={index}
                                       className="spec-row list-group list-group-horizontal"
                                    >
                                       <li className="list-group-item">
                                          {spec?.name}:
                                       </li>
                                       <li className="list-group-item">
                                          {spec?.value}
                                          {spec?.unit && ` ${spec?.unit}`}
                                       </li>
                                    </ul>
                                 ))}
                                 {isObjectWithKeys(
                                    specificationItems?.[activeTab]
                                       ?.manuals
                                 ) && (
                                    <FileList
                                       filesByGroup={
                                          specificationItems?.[activeTab]
                                             ?.manuals
                                       }
                                    />
                                 )}
                              </div>
                           </div>
                        ) : (
                           <>
                              {specificationItem?.specifications?.map(
                                 (spec, index) => (
                                    <ul
                                       key={index}
                                       className="spec-row list-group list-group-horizontal"
                                    >
                                       <li className="list-group-item">
                                          {spec?.name}:
                                       </li>
                                       <li className="list-group-item">
                                          {spec?.value}
                                          {spec?.unit && ` ${spec?.unit}`}
                                       </li>
                                    </ul>
                                 )
                              )}
                              {isObjectWithKeys(
                                 specificationItem?.manuals
                              ) && (
                                 <FileList
                                    filesByGroup={
                                       specificationItem.manuals
                                    }
                                 />
                              )}
                           </>
                        )}
                     </div>
                  </ProductSpecificationContainer>
               </ProductAccordion>
            </>
         )}
      </>
   )
}

const FileList = ({ filesByGroup }) => {
   const [isOpen, setIsOpen] = useState(false)
   const [iframeUrl, setIframeUrl] = useState('')
   const [drawerTitle, setDrawerTitle] = useState('')
   const isDesktop = useIsAboveLg()

   const handleOpenPdfDrawer = (url, title) => {
      setDrawerTitle(title)
      setIframeUrl(url)
      setIsOpen(true)
   }

   return (
      <>
         <VbDrawerPdf
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            iframeUrl={iframeUrl}
            title={drawerTitle}
            titleIcon={<PdfIcon style={{ height: '1.3em' }} />}
         >
            <iframe
               src={iframeUrl}
               style={{ width: '100%', height: '100%' }}
               title={drawerTitle}
            ></iframe>
         </VbDrawerPdf>

         <FilelistContainer>
            {Object.keys(filesByGroup).map((key, index) => {
               const fileList = filesByGroup[key]?.Files
               const filesGroupTitle = filesByGroup[key]?.title
               return (
                  <ul
                     className="spec-row list-group list-group-horizontal"
                     key={index}
                  >
                     <li className="list-group-item">{filesGroupTitle}</li>
                     <li className="list-group-item">
                        {fileList?.map((item, subIndex) => {
                           const fileUrl = item?.Url
                           return (
                              <>
                                 {isDesktop ? (
                                    <span
                                       className="desktop-pdf-icon"
                                       key={subIndex}
                                       onClick={() =>
                                          handleOpenPdfDrawer(
                                             fileUrl,
                                             filesGroupTitle
                                          )
                                       }
                                    >
                                       <PdfIcon
                                          style={{ cursor: 'pointer' }}
                                       ></PdfIcon>
                                    </span>
                                 ) : (
                                    <span
                                       className="mobile-pdf-icon"
                                       key={subIndex}
                                    >
                                       <a
                                          href={fileUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                       >
                                          <PdfIcon></PdfIcon>
                                       </a>
                                    </span>
                                 )}
                              </>
                           )
                        })}
                     </li>
                  </ul>
               )
            })}
         </FilelistContainer>
      </>
   )
}

export default ProductSpecification
